/**
 * ADMIN MODULE — Paperclip API Client
 * Single file for all Paperclip API communication.
 * Base URL set via environment variable — Tailscale in dev, Railway in prod.
 * Do not import this file from any module outside /admin.
 *
 * Paperclip runs as a SEPARATE process (never embedded in the app process).
 * The admin UI talks to it only through this client — components never call the
 * Paperclip API directly. Moving from Tailscale (dev) to Railway (prod) only
 * requires changing VITE_PAPERCLIP_URL; no code changes here.
 */

const PAPERCLIP_BASE_URL = import.meta.env.VITE_PAPERCLIP_URL;
const PAPERCLIP_COMPANY_ID = import.meta.env.VITE_PAPERCLIP_COMPANY_ID;

// Short per-request timeout so an unreachable Paperclip (e.g. Tailscale down)
// surfaces as a clean error/offline state instead of hanging the UI.
const DEFAULT_TIMEOUT_MS = 8000;

function configError() {
  return {
    data: null,
    error:
      "Paperclip is not configured. Set VITE_PAPERCLIP_URL and " +
      "VITE_PAPERCLIP_COMPANY_ID in app/.env.local.",
  };
}

// Thin fetch wrapper: builds the URL against the base, enforces a timeout, and
// normalises everything to { data, error }. Never throws to the caller.
async function request(path, { method = "GET", body, signal } = {}) {
  if (!PAPERCLIP_BASE_URL) return configError();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
  // Honour an externally-provided abort signal too (e.g. component unmount).
  if (signal) signal.addEventListener("abort", () => controller.abort(), { once: true });

  try {
    const res = await fetch(`${PAPERCLIP_BASE_URL}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    let payload = null;
    const text = await res.text();
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = text;
      }
    }

    if (!res.ok) {
      const msg =
        (payload && (payload.error || payload.message)) ||
        `Paperclip request failed (${res.status})`;
      console.error("[paperclipClient]", method, path, res.status, payload);
      return { data: null, error: msg };
    }

    return { data: payload, error: null };
  } catch (e) {
    const offline = e?.name === "AbortError";
    console.error("[paperclipClient]", method, path, e);
    return {
      data: null,
      error: offline
        ? "Paperclip not reachable — check your Tailscale connection."
        : "Could not reach Paperclip.",
    };
  } finally {
    clearTimeout(timer);
  }
}

// Company-scoped path helper. Returns null if the company id is missing.
function companyPath(suffix) {
  if (!PAPERCLIP_COMPANY_ID) return null;
  return `/api/companies/${encodeURIComponent(PAPERCLIP_COMPANY_ID)}${suffix}`;
}

/** GET /api/companies/:id/agents — all agents for the configured company. */
export async function getAgents() {
  const path = companyPath("/agents");
  if (!path) return configError();
  return request(path);
}

/** GET /api/companies/:id/issues?status= — issues, optionally filtered. */
export async function getIssues(status) {
  const path = companyPath("/issues");
  if (!path) return configError();
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return request(`${path}${qs}`);
}

/**
 * POST /api/companies/:id/issues — create an issue, optionally assigned.
 * Assigning to an agent with status 'todo' wakes it to work the task. We do NOT
 * send an X-Paperclip-Run-Id header: that's for agent-context calls and points at
 * a real heartbeat run; a board-initiated task has no run (Paperclip attributes it
 * to the implicit local "Board" user in local_trusted mode).
 */
export async function createIssue(title, description, agentId) {
  const path = companyPath("/issues");
  if (!path) return configError();
  const body = { title, description };
  if (agentId) {
    body.assigneeAgentId = agentId;
    body.status = "todo"; // assigned + todo → the agent picks it up
  }
  return request(path, { method: "POST", body });
}

/** Find an agent by (case-insensitive) name — used to resolve the writer agent id. */
export async function findAgentByName(name) {
  const { data, error } = await getAgents();
  if (error || !Array.isArray(data)) return { data: null, error: error || "No agents." };
  const want = String(name).toLowerCase();
  const hit = data.find((a) => (a.name || "").toLowerCase() === want)
    || data.find((a) => (a.name || "").toLowerCase().includes("writer"));
  return { data: hit || null, error: hit ? null : `Agent "${name}" not found.` };
}

/** GET /api/companies/:id/agents/:agentId/runs — recent runs for one agent. */
export async function getAgentRuns(agentId) {
  const path = companyPath(`/agents/${encodeURIComponent(agentId)}/runs`);
  if (!path) return configError();
  return request(path);
}

/** GET /api/health — liveness check used for the nav connection status. */
export async function getPaperclipHealth() {
  return request("/api/health");
}

/**
 * Dev-only: ask the Vite dev server to launch Paperclip locally (`npx paperclip run`).
 * A browser page can't start a local process — the dev server (a Node process)
 * does it via a middleware (see vite.config.js `paperclipLauncher`). This hits
 * `/__paperclip/start` directly (NOT the /paperclip proxy). In a production build
 * the endpoint doesn't exist, so this just returns an error — guard the UI with
 * `import.meta.env.DEV`.
 */
export async function startPaperclipServer() {
  try {
    const res = await fetch("/__paperclip/start", { method: "POST" });
    const text = await res.text();
    let payload = null;
    try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }
    if (!res.ok) {
      return { data: null, error: (payload && payload.error) || `Launcher failed (${res.status})` };
    }
    return { data: payload, error: null };
  } catch (e) {
    console.error("[paperclipClient] startPaperclipServer", e);
    return { data: null, error: "Local launcher unavailable (only works under npm run dev)." };
  }
}
